import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout, showToast } from '@components/Layout';
import { Button } from '@components/UI';
import { useAuth } from '@context/AuthContext';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { BETA_TESTER_AGREEMENT_ROUTE, sanitizeAgreementRedirectTarget } from '@/utils/agreements';

function getTwoFactorSetupRedirect(redirectTarget: string) {
    const sanitizedRedirectTarget = redirectTarget.startsWith('/user/enable2fa') ? '/user/portfolios' : redirectTarget;
    return `/user/enable2fa?redirect=${encodeURIComponent(sanitizedRedirectTarget)}`;
}

const BetaTesterAgreement = () => {
    const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
    const { isAuthenticated, isLoading, requiresTwoFactorSetup, hasPendingRequiredAgreements, refreshUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const redirectTarget = useMemo(() => {
        const query = new URLSearchParams(location.search);
        return sanitizeAgreementRedirectTarget(query.get('redirect'), BETA_TESTER_AGREEMENT_ROUTE);
    }, [location.search]);

    const agreementQuery = useQuery({
        ...trpc.auth.fetchActiveBetaAgreement.queryOptions(),
        retry: false,
        refetchOnWindowFocus: false
    });

    useEffect(() => {
        if (!agreementQuery.error) return;

        handleTrpcQuery(agreementQuery, {
            fallbackErrorMessage: 'Failed to load the Beta Tester Agreement',
            showErrorToast: true
        });
    }, [agreementQuery.error]);

    const acceptAgreementMutation = useTrpcMutation(trpc.auth.acceptAgreement.mutationOptions(), {
        fallbackErrorMessage: 'Failed to record Beta Tester Agreement acceptance'
    });

    const handleAcceptAgreement = async () => {
        if (!agreementQuery.data?.agreement) {
            showToast('The Beta Tester Agreement is still loading. Please try again in a moment.', 'error');
            return;
        }

        if (!hasAcceptedAgreement) {
            showToast('You must agree to the Beta Tester Agreement before continuing.', 'error');
            return;
        }

        const response = await acceptAgreementMutation.mutateAsync({
            agreementKey: agreementQuery.data.agreement.agreementKey,
            agreementVersion: agreementQuery.data.agreement.agreementVersion
        });

        if (!response.success) {
            return;
        }

        await refreshUser();
        showToast('Beta Tester Agreement accepted.', 'success');

        const nextRoute = requiresTwoFactorSetup ? getTwoFactorSetupRedirect(redirectTarget) : redirectTarget;
        void navigate(nextRoute, { replace: true });
    };

    if (!isLoading && isAuthenticated && !hasPendingRequiredAgreements && requiresTwoFactorSetup) {
        return <Navigate replace to={getTwoFactorSetupRedirect(redirectTarget)} />;
    }

    if (!isLoading && isAuthenticated && !hasPendingRequiredAgreements) {
        return <Navigate replace to={redirectTarget} />;
    }

    return (
        <Layout title="Project 314 - Beta Tester Agreement">
            <div className="container mx-auto my-5 max-w-5xl">
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-left shadow-lg">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-200">Private Beta</p>
                    <h1 className="mt-2 text-2xl font-semibold text-white">Beta Tester Agreement</h1>
                    <p className="mt-3 max-w-3xl text-sm leading-6 text-zinc-200">
                        Project314 is private beta software for live and simulated trading workflows. Review this agreement carefully before
                        using the app or deploying any strategy through it.
                    </p>
                </div>

                <div className="mt-5 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
                    <aside className="rounded-lg border border-zinc-700 bg-zinc-900/70 p-4 text-left shadow-lg">
                        <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-zinc-300">Key Risks</h2>
                        {agreementQuery.data?.agreement.summaryHighlights ? (
                            <ul className="mt-4 space-y-3 text-sm leading-6 text-zinc-200">
                                {agreementQuery.data.agreement.summaryHighlights.map((highlight) => (
                                    <li className="flex gap-3" key={highlight}>
                                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                                        <span>{highlight}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="mt-4 text-sm text-zinc-400">Loading agreement summary…</p>
                        )}

                        <div className="mt-5 border-t border-zinc-700 pt-4 text-xs leading-5 text-zinc-400">
                            <p>
                                This agreement supplements the{' '}
                                <Link className="text-blue-300 underline underline-offset-2 hover:text-blue-200" to="/terms-and-conditions">
                                    Terms and Conditions
                                </Link>{' '}
                                and{' '}
                                <Link className="text-blue-300 underline underline-offset-2 hover:text-blue-200" to="/privacy-policy">
                                    Privacy Policy
                                </Link>
                                .
                            </p>
                        </div>
                    </aside>

                    <section className="rounded-lg border border-zinc-700 bg-zinc-900/70 p-5 text-left shadow-lg">
                        {agreementQuery.data?.agreement ? (
                            <div className="space-y-5">
                                <div className="rounded-lg border border-zinc-700 bg-zinc-950/50 p-4 text-sm text-zinc-300">
                                    <p>
                                        Effective date: <span className="text-white">{agreementQuery.data.agreement.effectiveDate}</span>
                                    </p>
                                    <p className="mt-2">
                                        Version: <span className="text-white">{agreementQuery.data.agreement.agreementVersion}</span>
                                    </p>
                                </div>

                                <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-950/50 p-5">
                                    <div className="leading-relaxed text-zinc-100">
                                        <ReactMarkdown
                                            skipHtml
                                            components={{
                                                h1: ({ children }) => (
                                                    <h1 className="mt-4 first:mt-0 mb-2 text-xl font-semibold text-white">{children}</h1>
                                                ),
                                                h2: ({ children }) => (
                                                    <h2 className="mt-5 mb-2 text-base font-semibold text-white">{children}</h2>
                                                ),
                                                p: ({ children }) => <p className="my-3 text-sm leading-6 text-zinc-200">{children}</p>,
                                                ul: ({ children }) => (
                                                    <ul className="my-3 ml-5 list-disc space-y-2 text-sm text-zinc-200">{children}</ul>
                                                ),
                                                li: ({ children }) => <li>{children}</li>
                                            }}
                                            remarkPlugins={[remarkGfm]}
                                        >
                                            {agreementQuery.data.agreement.markdown}
                                        </ReactMarkdown>
                                    </div>
                                </div>

                                {isAuthenticated ? (
                                    <div className="rounded-lg border border-zinc-700 bg-zinc-950/50 p-4">
                                        <label
                                            className="flex items-start gap-3 text-sm leading-6 text-zinc-200"
                                            htmlFor="acceptBetaAgreement"
                                        >
                                            <input
                                                checked={hasAcceptedAgreement}
                                                className="mt-1 h-4 w-4 rounded border-zinc-500 bg-zinc-900 text-blue-500 focus:ring-blue-500"
                                                id="acceptBetaAgreement"
                                                type="checkbox"
                                                onChange={(e) => setHasAcceptedAgreement(e.target.checked)}
                                            />
                                            <span>
                                                I have read and agree to the Beta Tester Agreement, including the risks of inaccurate data,
                                                software defects, failed automations, and losing money by using this platform.
                                            </span>
                                        </label>

                                        <div className="mt-4 flex flex-wrap gap-3">
                                            <Button
                                                disabled={acceptAgreementMutation.isPending || !agreementQuery.data?.agreement}
                                                type="button"
                                                onClick={handleAcceptAgreement}
                                            >
                                                {acceptAgreementMutation.isPending ? 'Saving…' : 'Agree and Continue'}
                                            </Button>
                                            <Button type="button" variant="outline-dark" onClick={() => navigate(-1)}>
                                                Back
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-zinc-700 bg-zinc-950/50 p-4 text-sm leading-6 text-zinc-300">
                                        Review the agreement above before creating an account. Acceptance is required during registration
                                        and will be required for existing beta users the next time they log in.
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="rounded-lg border border-zinc-700 bg-zinc-950/50 p-5 text-sm text-zinc-400">
                                Loading Beta Tester Agreement…
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </Layout>
    );
};

export default BetaTesterAgreement;
